/**
 * Firebase client SDK bootstrap.
 *
 * Reads `VITE_FIREBASE_*` env vars at module load. Initialisation is idempotent
 * via `getApps().length` — calling `initializeFirebase()` more than once is
 * safe (HMR, tests).
 *
 * Tests mock the underlying `firebase/app` and `firebase/auth` modules so the
 * env vars are not required.
 */
import { getApp, getApps, initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import type { FirebaseApp } from "firebase/app";
import type { Auth } from "firebase/auth";

interface FirebaseEnv {
  VITE_FIREBASE_API_KEY?: string;
  VITE_FIREBASE_AUTH_DOMAIN?: string;
  VITE_FIREBASE_PROJECT_ID?: string;
  VITE_FIREBASE_APP_ID?: string;
}

function readEnv(): FirebaseEnv {
  // import.meta.env is typed loosely; Vite supplies VITE_* keys at build time.
  return (import.meta as { env?: FirebaseEnv }).env ?? {};
}

let appSingleton: FirebaseApp | null = null;

export function getFirebaseApp(): FirebaseApp {
  if (appSingleton) return appSingleton;
  if (getApps().length > 0) {
    appSingleton = getApp();
    return appSingleton;
  }
  const env = readEnv();
  appSingleton = initializeApp({
    apiKey: env.VITE_FIREBASE_API_KEY ?? "",
    authDomain: env.VITE_FIREBASE_AUTH_DOMAIN ?? "",
    projectId: env.VITE_FIREBASE_PROJECT_ID ?? "",
    appId: env.VITE_FIREBASE_APP_ID ?? "",
  });
  return appSingleton;
}

export function getFirebaseAuth(): Auth {
  return getAuth(getFirebaseApp());
}
